import type { ExcalidrawElementSkeleton } from "@excalidraw/element";

type Mutable<T> = {
  -readonly [Key in keyof T]: T[Key];
};

type GeneratedElement = Mutable<ExcalidrawElementSkeleton> &
  Record<string, any>;

type CardInfo = {
  element: GeneratedElement;
  index: number;
  textIndexes: number[];
  role: "main" | "strategic" | "org" | "next";
};

type EssentialCard = {
  title: string;
  bullets: string[];
  keywords: string[];
};

const TEXT_COLOR = "#1e1e1e";
const CARD_BACKGROUNDS = [
  "#dbeafe",
  "#dcfce7",
  "#ede9fe",
  "#fef3c7",
  "#f8fafc",
];
const CARD_STROKES = ["#2563eb", "#16a34a", "#7c3aed", "#f59e0b", "#475569"];

const CARD_TYPES = new Set(["rectangle", "diamond"]);
const MIN_CARD_WIDTH = 320;
const MIN_CARD_HEIGHT = 160;
const CARD_PADDING = 28;
const TEXT_GAP = 14;

const EXECUTIVE_KEYWORDS = [
  "apresentacao",
  "comercial",
  "gestao",
  "operacao",
  "organograma",
  "estrutura",
  "diagnostico",
  "indicadores",
  "kpis",
  "plano",
  "perguntas",
  "proximos",
  "vendas",
  "gargalos",
  "rotina",
  "automacao",
  "responsaveis",
  "chefes",
];

const ESSENTIAL_EXECUTIVE_CARDS: EssentialCard[] = [
  {
    title: "Diagnostico da operacao",
    bullets: ["Mapear origem dos leads", "Identificar perdas por etapa"],
    keywords: ["diagnostico", "operacao atual"],
  },
  {
    title: "Estrutura comercial ideal",
    bullets: [
      "Definir papéis e responsáveis",
      "Separar SDR, vendas e pós-venda",
    ],
    keywords: ["estrutura comercial", "estrutura ideal"],
  },
  {
    title: "Processo de vendas",
    bullets: ["Padronizar etapas do funil", "Criar critérios de passagem"],
    keywords: ["processo de vendas", "processo comercial"],
  },
  {
    title: "Atendimento e relacionamento",
    bullets: ["Responder rápido e com padrão", "Criar rotina de follow-up"],
    keywords: ["atendimento", "relacionamento"],
  },
  {
    title: "Indicadores/KPIs",
    bullets: [
      "Leads, agendamentos e conversão",
      "Ticket médio e receita prevista",
    ],
    keywords: ["indicadores", "kpis", "metricas"],
  },
  {
    title: "Plano de acao",
    bullets: ["Priorizar gargalos críticos", "Executar melhorias semanais"],
    keywords: ["plano de acao", "acoes"],
  },
  {
    title: "Perguntas estrategicas",
    bullets: ["Onde perdemos leads?", "Quem acompanha cada etapa?"],
    keywords: ["perguntas estrategicas", "perguntas"],
  },
  {
    title: "Gargalos provaveis",
    bullets: ["Resposta lenta", "Follow-up irregular"],
    keywords: ["gargalos", "gargalos provaveis"],
  },
  {
    title: "Responsaveis por etapa",
    bullets: ["SDR qualifica leads", "Closer conduz propostas"],
    keywords: ["responsaveis", "responsaveis por etapa"],
  },
  {
    title: "Proximos passos",
    bullets: ["Validar responsáveis", "Acompanhar metas semanalmente"],
    keywords: ["proximos passos", "proximos"],
  },
  {
    title: "Rotina semanal",
    bullets: ["Revisar funil e gargalos", "Ajustar scripts e metas"],
    keywords: ["rotina semanal", "rotina"],
  },
  {
    title: "Oportunidades de automacao",
    bullets: ["Lembretes de follow-up", "Dashboard e CRM atualizados"],
    keywords: ["automacao", "oportunidades de automacao"],
  },
  {
    title: "Diretoria / Gestao",
    bullets: ["Define metas", "Remove bloqueios"],
    keywords: ["diretoria"],
  },
  {
    title: "Coordenacao Comercial",
    bullets: ["Acompanha rotina", "Cobra proximas acoes"],
    keywords: ["coordenacao", "coordenacao comercial"],
  },
  {
    title: "Pre-vendas / SDR",
    bullets: ["Qualifica leads", "Agenda reunioes"],
    keywords: ["pre-vendas", "sdr"],
  },
  {
    title: "Vendas / Closer",
    bullets: ["Diagnostica necessidade", "Conduz proposta"],
    keywords: ["closer"],
  },
  {
    title: "Pos-venda / Sucesso do Cliente",
    bullets: ["Retem clientes", "Gera recompra"],
    keywords: ["pos-venda", "sucesso do cliente"],
  },
];

const normalizeText = (value: string) => {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
};

const getElementText = (element: GeneratedElement) => {
  const textParts = [];

  if (typeof element.text === "string") {
    textParts.push(element.text);
  }

  if (
    element.label &&
    typeof element.label === "object" &&
    typeof element.label.text === "string"
  ) {
    textParts.push(element.label.text);
  }

  return textParts.join("\n");
};

const getNumber = (value: unknown, fallback: number) => {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
};

const getBounds = (element: GeneratedElement) => {
  const x = getNumber(element.x, 0);
  const y = getNumber(element.y, 0);
  const width = getNumber(element.width, 0);
  const height = getNumber(element.height, 0);

  return {
    x,
    y,
    width,
    height,
    centerX: x + width / 2,
    centerY: y + height / 2,
  };
};

const getAbsoluteBounds = (element: GeneratedElement) => {
  const x = getNumber(element.x, 0);
  const y = getNumber(element.y, 0);
  const width = getNumber(element.width, 0);
  const height = getNumber(element.height, 0);

  return {
    minX: Math.min(x, x + width),
    minY: Math.min(y, y + height),
    maxX: Math.max(x, x + width),
    maxY: Math.max(y, y + height),
  };
};

const isPointInsideExpandedBounds = (
  point: { x: number; y: number },
  element: GeneratedElement,
  expansion: number,
) => {
  const bounds = getBounds(element);

  return (
    point.x >= bounds.x - expansion &&
    point.x <= bounds.x + bounds.width + expansion &&
    point.y >= bounds.y - expansion &&
    point.y <= bounds.y + bounds.height + expansion
  );
};

const estimateLineCount = (text: string, width: number, fontSize: number) => {
  const charsPerLine = Math.max(16, Math.floor(width / (fontSize * 0.52)));

  return text.split("\n").reduce((lineCount, line) => {
    return lineCount + Math.max(1, Math.ceil(line.length / charsPerLine));
  }, 0);
};

const getTextHeight = (
  element: GeneratedElement,
  width: number,
  fallbackFontSize: number,
) => {
  const fontSize = getNumber(element.fontSize, fallbackFontSize);
  const text = getElementText(element) || "Texto";
  const lineCount = estimateLineCount(text, width, fontSize);

  return Math.ceil(lineCount * fontSize * 1.3);
};

const getCardText = (card: CardInfo, elements: readonly GeneratedElement[]) => {
  const parts = [getElementText(card.element)];

  for (const textIndex of card.textIndexes) {
    parts.push(getElementText(elements[textIndex]));
  }

  return normalizeText(parts.filter(Boolean).join("\n"));
};

const classifyCard = (
  card: CardInfo,
  elements: readonly GeneratedElement[],
): CardInfo["role"] => {
  const text = getCardText(card, elements);

  if (
    /\b(organograma|diretoria|coordena|pre-vendas|sdr|closer|pos-venda|sucesso do cliente)\b/.test(
      text,
    )
  ) {
    return "org";
  }

  if (/\b(proximos|passos)\b/.test(text)) {
    return "next";
  }

  if (
    /\b(perguntas|gargalos|responsaveis|rotina|automacao|automatizado)\b/.test(
      text,
    )
  ) {
    return "strategic";
  }

  return "main";
};

const hasExecutiveBoardShape = (elements: readonly GeneratedElement[]) => {
  const cardCount = elements.filter((element) =>
    CARD_TYPES.has(element.type),
  ).length;
  const text = normalizeText(elements.map(getElementText).join("\n"));
  const keywordMatches = EXECUTIVE_KEYWORDS.filter((keyword) =>
    text.includes(keyword),
  ).length;

  return cardCount >= 3 && keywordMatches >= 2;
};

const getTitleIndexes = (elements: readonly GeneratedElement[]) => {
  const textEntries = elements
    .map((element, index) => ({ element, index }))
    .filter(({ element }) => element.type === "text")
    .sort((first, second) => {
      const fontDifference =
        getNumber(second.element.fontSize, 0) -
        getNumber(first.element.fontSize, 0);

      return (
        fontDifference ||
        getNumber(first.element.y, 0) - getNumber(second.element.y, 0)
      );
    });

  const titleIndex = textEntries[0]?.index ?? null;
  const subtitleIndex =
    textEntries.find(({ index, element }) => {
      return (
        index !== titleIndex &&
        getNumber(element.fontSize, 0) >= 18 &&
        getNumber(element.fontSize, 0) <= 30
      );
    })?.index ?? null;

  return { titleIndex, subtitleIndex };
};

const createFallbackId = (index: number, suffix: string) => {
  return `ai_layout_fallback_${index}_${suffix}`;
};

const hasEssentialCard = (
  elements: readonly GeneratedElement[],
  essentialCard: EssentialCard,
) => {
  const text = normalizeText(elements.map(getElementText).join("\n"));

  return essentialCard.keywords.some((keyword) =>
    text.includes(normalizeText(keyword)),
  );
};

const ensureEssentialExecutiveCards = (elements: GeneratedElement[]) => {
  const missingCards = ESSENTIAL_EXECUTIVE_CARDS.filter(
    (essentialCard) => !hasEssentialCard(elements, essentialCard),
  );

  missingCards.forEach((essentialCard, missingIndex) => {
    const x = 200 + (missingIndex % 3) * 40;
    const y = 300 + Math.floor(missingIndex / 3) * 40;
    const cardIndex = elements.length + missingIndex;
    const cardId = createFallbackId(cardIndex, "card");
    const textId = createFallbackId(cardIndex, "text");

    elements.push(
      {
        id: cardId,
        type: "rectangle",
        x,
        y,
        width: 560,
        height: 190,
        strokeColor: CARD_STROKES[cardIndex % CARD_STROKES.length],
        backgroundColor: CARD_BACKGROUNDS[cardIndex % CARD_BACKGROUNDS.length],
        fillStyle: "solid",
        strokeWidth: 3,
        roughness: 0.55,
        opacity: 100,
        isDeleted: false,
      } as GeneratedElement,
      {
        id: textId,
        type: "text",
        x: x + CARD_PADDING,
        y: y + CARD_PADDING,
        width: 560 - CARD_PADDING * 2,
        height: 120,
        strokeColor: TEXT_COLOR,
        backgroundColor: "transparent",
        fontSize: 20,
        fontFamily: 1,
        text: `${essentialCard.title}\n- ${essentialCard.bullets.join("\n- ")}`,
        textAlign: "left",
        verticalAlign: "top",
        opacity: 100,
        isDeleted: false,
      } as GeneratedElement,
    );
  });
};

const associateTextsToCards = (
  elements: readonly GeneratedElement[],
  titleIndex: number | null,
  subtitleIndex: number | null,
) => {
  const cards = elements
    .map((element, index) => ({ element, index }))
    .filter(({ element }) => CARD_TYPES.has(element.type))
    .map<CardInfo>(({ element, index }) => ({
      element,
      index,
      textIndexes: [],
      role: "main",
    }));

  const textIndexes = elements
    .map((element, index) => ({ element, index }))
    .filter(({ element, index }) => {
      return (
        element.type === "text" &&
        index !== titleIndex &&
        index !== subtitleIndex
      );
    })
    .map(({ index }) => index);

  for (const textIndex of textIndexes) {
    const textBounds = getBounds(elements[textIndex]);
    const textPoint = {
      x: textBounds.centerX,
      y: textBounds.centerY,
    };

    let bestCard: CardInfo | null = null;
    let bestDistance = Number.POSITIVE_INFINITY;

    for (const card of cards) {
      const cardBounds = getBounds(card.element);
      const isInside = isPointInsideExpandedBounds(textPoint, card.element, 80);
      const distance = Math.hypot(
        textPoint.x - cardBounds.centerX,
        textPoint.y - cardBounds.centerY,
      );

      if ((isInside || distance < 260) && distance < bestDistance) {
        bestCard = card;
        bestDistance = distance;
      }
    }

    if (bestCard) {
      bestCard.textIndexes.push(textIndex);
    }
  }

  for (const card of cards) {
    card.role = classifyCard(card, elements);
  }

  return cards;
};

const calculateCardHeight = (
  card: CardInfo,
  elements: readonly GeneratedElement[],
  width: number,
) => {
  const textWidth = width - CARD_PADDING * 2;
  const textHeights = card.textIndexes.map((textIndex, textPosition) => {
    return getTextHeight(
      elements[textIndex],
      textWidth,
      textPosition ? 18 : 22,
    );
  });
  const labelText = getElementText(card.element);
  const labelHeight = labelText
    ? estimateLineCount(labelText, textWidth, 22) * 30
    : 0;
  const totalTextHeight = [...textHeights, labelHeight].reduce(
    (sum, height) => sum + height,
    0,
  );
  const gaps = Math.max(0, textHeights.length - 1) * TEXT_GAP;

  return Math.max(
    MIN_CARD_HEIGHT,
    getNumber(card.element.height, MIN_CARD_HEIGHT),
    CARD_PADDING * 2 + totalTextHeight + gaps,
  );
};

const placeTextInsideCard = (
  card: CardInfo,
  elements: GeneratedElement[],
  width: number,
) => {
  let cursorY = getNumber(card.element.y, 0) + CARD_PADDING;
  const textWidth = width - CARD_PADDING * 2;

  card.textIndexes
    .slice()
    .sort((firstIndex, secondIndex) => {
      return (
        getNumber(elements[firstIndex].y, 0) -
        getNumber(elements[secondIndex].y, 0)
      );
    })
    .forEach((textIndex, textPosition) => {
      const textElement = elements[textIndex];
      const fontSize = Math.max(
        getNumber(textElement.fontSize, 18),
        textPosition ? 18 : 22,
      );
      const height = getTextHeight(textElement, textWidth, fontSize);

      textElement.x = getNumber(card.element.x, 0) + CARD_PADDING;
      textElement.y = cursorY;
      textElement.width = textWidth;
      textElement.height = height;
      textElement.fontSize = fontSize;
      textElement.strokeColor = TEXT_COLOR;
      textElement.backgroundColor = "transparent";
      textElement.textAlign = "left";
      textElement.verticalAlign = "top";
      textElement.lineHeight = textPosition ? 1.35 : 1.3;

      cursorY += height + TEXT_GAP;
    });
};

const placeCard = (
  card: CardInfo,
  elements: GeneratedElement[],
  layout: { x: number; y: number; width: number; height: number },
  paletteIndex: number,
) => {
  card.element.x = layout.x;
  card.element.y = layout.y;
  card.element.width = layout.width;
  card.element.height = layout.height;
  card.element.opacity = 100;
  card.element.fillStyle = "solid";
  card.element.roughness = 0.55;
  card.element.backgroundColor =
    CARD_BACKGROUNDS[paletteIndex % CARD_BACKGROUNDS.length];
  card.element.strokeColor = CARD_STROKES[paletteIndex % CARD_STROKES.length];
  card.element.strokeWidth = Math.max(
    getNumber(card.element.strokeWidth, 2),
    3,
  );

  placeTextInsideCard(card, elements, layout.width);
};

const placeTitle = (
  element: GeneratedElement,
  layout: { x: number; y: number; width: number; height: number },
  fontSize: number,
) => {
  element.x = layout.x;
  element.y = layout.y;
  element.width = layout.width;
  element.height = layout.height;
  element.fontSize = fontSize;
  element.strokeColor = TEXT_COLOR;
  element.backgroundColor = "transparent";
  element.textAlign = "center";
  element.verticalAlign = "middle";
};

const estimateTextWidth = (
  element: GeneratedElement,
  fallbackWidth: number,
  maxWidth: number,
) => {
  const text = getElementText(element);
  const fontSize = getNumber(element.fontSize, 32);
  const estimatedWidth = text.length * fontSize * 0.58 + fontSize * 2;

  return Math.min(Math.max(estimatedWidth, fallbackWidth), maxWidth);
};

const getBoardContentBounds = (
  elements: readonly GeneratedElement[],
  titleIndex: number | null,
  subtitleIndex: number | null,
) => {
  const contentBounds = elements
    .map((element, index) => ({ element, index }))
    .filter(({ element, index }) => {
      return (
        index !== titleIndex &&
        index !== subtitleIndex &&
        element.isDeleted !== true &&
        getNumber(element.opacity, 100) > 0 &&
        getNumber(element.width, 0) !== 0
      );
    })
    .map(({ element }) => getAbsoluteBounds(element));

  if (!contentBounds.length) {
    return null;
  }

  return contentBounds.reduce(
    (bounds, elementBounds) => ({
      minX: Math.min(bounds.minX, elementBounds.minX),
      minY: Math.min(bounds.minY, elementBounds.minY),
      maxX: Math.max(bounds.maxX, elementBounds.maxX),
      maxY: Math.max(bounds.maxY, elementBounds.maxY),
    }),
    contentBounds[0],
  );
};

const centerBoardTitle = (
  elements: GeneratedElement[],
  titleIndex: number | null,
  subtitleIndex: number | null,
) => {
  const contentBounds = getBoardContentBounds(
    elements,
    titleIndex,
    subtitleIndex,
  );

  if (!contentBounds) {
    return;
  }

  const centerX = (contentBounds.minX + contentBounds.maxX) / 2;
  const contentWidth = contentBounds.maxX - contentBounds.minX;
  const titleElement = titleIndex !== null ? elements[titleIndex] : null;
  const subtitleElement =
    subtitleIndex !== null ? elements[subtitleIndex] : null;
  const titleHeight = titleElement ? getNumber(titleElement.height, 66) : 66;
  const subtitleHeight = subtitleElement
    ? getNumber(subtitleElement.height, 44)
    : 44;
  const topY = Math.max(
    36,
    contentBounds.minY - titleHeight - subtitleHeight - 64,
  );

  if (titleElement) {
    titleElement.width = estimateTextWidth(
      titleElement,
      Math.min(760, contentWidth),
      contentWidth,
    );
    titleElement.x = centerX - titleElement.width / 2;
    titleElement.y = topY;
    titleElement.textAlign = "center";
    titleElement.verticalAlign = "middle";
  }

  if (subtitleElement) {
    subtitleElement.width = estimateTextWidth(
      subtitleElement,
      Math.min(920, contentWidth),
      contentWidth,
    );
    subtitleElement.x = centerX - subtitleElement.width / 2;
    subtitleElement.y =
      (titleElement
        ? getNumber(titleElement.y, topY) + getNumber(titleElement.height, 56)
        : topY) + 18;
    subtitleElement.textAlign = "center";
    subtitleElement.verticalAlign = "middle";
  }
};

const layoutArrows = (
  elements: GeneratedElement[],
  cards: readonly CardInfo[],
  orgCards: readonly CardInfo[],
) => {
  const arrows = elements.filter(
    (element) => element.type === "arrow" || element.type === "line",
  );
  const connectCards = (
    arrow: GeneratedElement,
    fromCard: CardInfo,
    toCard: CardInfo,
  ) => {
    const from = getBounds(fromCard.element);
    const to = getBounds(toCard.element);
    const startX = from.centerX;
    const startY = from.y + from.height;
    const endX = to.centerX;
    const endY = to.y;

    arrow.x = startX;
    arrow.y = startY;
    arrow.width = endX - startX;
    arrow.height = endY - startY;
    arrow.points = [
      [0, 0],
      [arrow.width, arrow.height],
    ];
    arrow.strokeColor = TEXT_COLOR;
    arrow.strokeWidth = 2;
    arrow.opacity = 100;
    arrow.endArrowhead = "arrow";
  };

  if (orgCards.length >= 3 && arrows.length > 0) {
    const orgConnections = [
      [orgCards[0], orgCards[1]],
      [orgCards[1], orgCards[2]],
      [orgCards[1], orgCards[3]],
      [orgCards[1], orgCards[4]],
    ].filter(([fromCard, toCard]) => fromCard && toCard);

    arrows.forEach((arrow, index) => {
      const connection = orgConnections[index];

      if (!connection) {
        arrow.opacity = 0;
        return;
      }

      connectCards(arrow, connection[0], connection[1]);
    });
    return;
  }

  const visibleCards = cards.filter(
    (card) => getNumber(card.element.width, 0) > 0,
  );

  arrows.forEach((arrow, index) => {
    const fromCard = visibleCards[index % Math.max(1, visibleCards.length)];
    const toCard = visibleCards[(index + 1) % Math.max(1, visibleCards.length)];

    if (!fromCard || !toCard || fromCard === toCard) {
      arrow.opacity = 0;
      return;
    }

    connectCards(arrow, fromCard, toCard);
  });
};

export const layoutGeneratedBoard = (
  sourceElements: readonly ExcalidrawElementSkeleton[],
): ExcalidrawElementSkeleton[] => {
  const elements = sourceElements.map((element) => ({
    ...(element as GeneratedElement),
  }));
  const { titleIndex, subtitleIndex } = getTitleIndexes(elements);

  if (!hasExecutiveBoardShape(elements)) {
    centerBoardTitle(elements, titleIndex, subtitleIndex);
    return elements;
  }

  ensureEssentialExecutiveCards(elements);
  const cards = associateTextsToCards(elements, titleIndex, subtitleIndex);
  const usedCards = new Set<CardInfo>();
  const mainCards = cards.filter((card) => card.role === "main").slice(0, 6);
  mainCards.forEach((card) => usedCards.add(card));

  const strategicCandidates = [
    ...cards.filter((card) => card.role === "strategic"),
    ...cards.filter((card) => card.role === "main" && !usedCards.has(card)),
  ];
  const strategicCards = strategicCandidates.slice(0, 4);
  strategicCards.forEach((card) => usedCards.add(card));

  const orgCards = cards.filter((card) => card.role === "org");
  orgCards.forEach((card) => usedCards.add(card));

  const nextCards = cards.filter((card) => card.role === "next");
  nextCards.forEach((card) => usedCards.add(card));

  const overflowCards = cards.filter((card) => !usedCards.has(card));
  const orderedCards = [
    ...mainCards,
    ...strategicCards,
    ...orgCards,
    ...nextCards,
    ...overflowCards,
  ];

  if (titleIndex !== null) {
    placeTitle(
      elements[titleIndex],
      { x: 80, y: 36, width: 1840, height: 66 },
      42,
    );
  }

  if (subtitleIndex !== null) {
    placeTitle(
      elements[subtitleIndex],
      { x: 180, y: 112, width: 1640, height: 44 },
      24,
    );
  }

  const mainWidth = Math.max(MIN_CARD_WIDTH, 560);
  const strategicWidth = Math.max(MIN_CARD_WIDTH, 900);
  const orgWidth = 320;
  const nextWidth = 1840;
  const gapX = 80;
  const gapY = 70;
  const startX = 200;
  const startY = 300;
  const rowOne = mainCards.slice(0, 3);
  const rowTwo = mainCards.slice(3, 6);
  const firstRowHeight = Math.max(
    230,
    ...rowOne.map((card) => calculateCardHeight(card, elements, mainWidth)),
  );
  const secondRowHeight = Math.max(
    230,
    ...rowTwo.map((card) => calculateCardHeight(card, elements, mainWidth)),
  );

  rowOne.forEach((card, index) => {
    placeCard(
      card,
      elements,
      {
        x: startX + index * (mainWidth + gapX),
        y: startY,
        width: mainWidth,
        height: firstRowHeight,
      },
      index,
    );
  });

  rowTwo.forEach((card, index) => {
    placeCard(
      card,
      elements,
      {
        x: startX + index * (mainWidth + gapX),
        y: startY + firstRowHeight + gapY,
        width: mainWidth,
        height: secondRowHeight,
      },
      index + 3,
    );
  });

  const strategicStartY = startY + firstRowHeight + gapY + secondRowHeight + 64;
  const strategicRowOne = strategicCards.slice(0, 2);
  const strategicRowTwo = strategicCards.slice(2, 4);
  const strategicFirstRowHeight = Math.max(
    220,
    ...strategicRowOne.map((card) =>
      calculateCardHeight(card, elements, strategicWidth),
    ),
  );
  const strategicSecondRowHeight = Math.max(
    220,
    ...strategicRowTwo.map((card) =>
      calculateCardHeight(card, elements, strategicWidth),
    ),
  );

  strategicRowOne.forEach((card, index) => {
    placeCard(
      card,
      elements,
      {
        x: startX + index * (strategicWidth + gapX),
        y: strategicStartY,
        width: strategicWidth,
        height: strategicFirstRowHeight,
      },
      index + 6,
    );
  });

  strategicRowTwo.forEach((card, index) => {
    placeCard(
      card,
      elements,
      {
        x: startX + index * (strategicWidth + gapX),
        y: strategicStartY + strategicFirstRowHeight + gapY,
        width: strategicWidth,
        height: strategicSecondRowHeight,
      },
      index + 8,
    );
  });

  const orgStartY =
    strategicStartY +
    strategicFirstRowHeight +
    (strategicRowTwo.length ? gapY + strategicSecondRowHeight : 0) +
    92;
  const orgRowHeight = Math.max(
    142,
    ...orgCards.map((card) => calculateCardHeight(card, elements, orgWidth)),
  );

  orgCards.forEach((card, index) => {
    const orgLayouts = [
      { x: startX + 710, y: orgStartY },
      { x: startX + 710, y: orgStartY + orgRowHeight + 72 },
      { x: startX + 280, y: orgStartY + (orgRowHeight + 72) * 2 },
      { x: startX + 710, y: orgStartY + (orgRowHeight + 72) * 2 },
      { x: startX + 1140, y: orgStartY + (orgRowHeight + 72) * 2 },
    ];
    const overflowColumn = (index - orgLayouts.length) % 3;
    const overflowRow = Math.floor(Math.max(0, index - orgLayouts.length) / 3);
    const layout = orgLayouts[index] ?? {
      x: startX + overflowColumn * (orgWidth + 70),
      y: orgStartY + (orgRowHeight + 72) * (3 + overflowRow),
    };

    placeCard(
      card,
      elements,
      {
        x: layout.x,
        y: layout.y,
        width: orgWidth,
        height: orgRowHeight,
      },
      index + 10,
    );
  });

  const orgRows = Math.max(1, Math.ceil(orgCards.length / 5));
  const nextStartY =
    orgStartY + Math.max(3, orgRows) * (orgRowHeight + 72) + 48;
  const nextAreaCards = [...nextCards, ...overflowCards];

  nextAreaCards.forEach((card, index) => {
    const isFirstNextCard = index === 0 && nextCards.includes(card);
    const width = isFirstNextCard ? nextWidth : mainWidth;
    const height = Math.max(
      isFirstNextCard ? 170 : 180,
      calculateCardHeight(card, elements, width),
    );
    const row = isFirstNextCard ? 0 : Math.floor((index - 1) / 3) + 1;
    const column = isFirstNextCard ? 0 : (index - 1) % 3;

    placeCard(
      card,
      elements,
      {
        x: isFirstNextCard ? startX : startX + column * (mainWidth + gapX),
        y: nextStartY + row * (height + gapY + 8),
        width,
        height,
      },
      index + 15,
    );
  });

  layoutArrows(elements, orderedCards, orgCards);
  centerBoardTitle(elements, titleIndex, subtitleIndex);

  return elements;
};
